import api from './api';

class AuthService {
  // Regular email/password login
  async login(email, password) {
    try {
      // console.log('Login attempt:', { email, userAgent: navigator.userAgent });
      
      const response = await api.post('/api/data_predator/login/', {
        email,
        password
      });
      
      // console.log('Login response:', response.status, response.data);
      
      if (response.data.token) {
        this.setTokenWithExpiration(response.data.token);
        this.setItem('user', JSON.stringify({ email }));
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error details:', {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers,
        userAgent: navigator.userAgent
      });
      throw error.response?.data || { error: 'Login failed' };
    }
  }

  // Google OAuth login with access token (legacy method)
  async googleLogin(accessToken) {
    try {
      const response = await api.post('/api/data_predator/google-oauth2-login/', {
        access_token: accessToken
      });
      
      if (response.data.token) {
        this.setTokenWithExpiration(response.data.token);
        this.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Google login failed' };
    }
  }

  // Google OAuth login with JWT credential (new method)
  async googleLoginWithCredential(credential) {
    try {
      const response = await api.post('/api/data_predator/google-oauth2-login/', {
        credential: credential
      });
      
      if (response.data.token) {
        this.setTokenWithExpiration(response.data.token);
        this.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Google login failed' };
    }
  }

  // Get Google OAuth URL
  async getGoogleAuthUrl() {
    try {
      const response = await api.get('/api/data_predator/google-oauth2-url/');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get Google auth URL' };
    }
  }

  // Storage helper method (삼성 브라우저 호환성)
  setItem(key, value) {
    try {
      localStorage.setItem(key, value);
      // 삼성 브라우저에서 LocalStorage 검증
      const stored = localStorage.getItem(key);
      if (!stored) {
        console.warn('LocalStorage may be blocked. Trying sessionStorage...');
        sessionStorage.setItem(key, value);
      }
    } catch (error) {
      console.error('Storage error:', error);
      try {
        sessionStorage.setItem(key, value);
      } catch (sessionError) {
        console.error('SessionStorage also failed:', sessionError);
        // Fallback: 메모리에 임시 저장
        if (!window.tempStorage) window.tempStorage = {};
        window.tempStorage[key] = value;
      }
    }
  }

  // Storage helper method for getting items
  getItem(key) {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key) || window.tempStorage?.[key] || null;
    } catch (error) {
      console.error('Storage read error:', error);
      return window.tempStorage?.[key] || null;
    }
  }

  // Storage helper method for removing items
  removeItem(key) {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
      if (window.tempStorage) {
        delete window.tempStorage[key];
      }
    } catch (error) {
      console.error('Storage remove error:', error);
    }
  }

  // Set token with 6-hour expiration
  setTokenWithExpiration(token) {
    const now = new Date();
    const expirationTime = now.getTime() + (6 * 60 * 60 * 1000); // 6 hours in milliseconds
    
    this.setItem('token', token);
    this.setItem('tokenExpiration', expirationTime.toString());
  }

  // Check if token is expired
  isTokenExpired() {
    const expirationTime = this.getItem('tokenExpiration');
    if (!expirationTime) {
      // If no expiration time is set, this might be an old token
      // Set expiration time for existing token (6 hours from now)
      const token = this.getItem('token');
      if (token) {
        this.setTokenWithExpiration(token);
        return false; // Token is now valid for 6 hours
      }
      return true;
    }
    
    const now = new Date().getTime();
    return now > parseInt(expirationTime);
  }

  // Logout
  async logout() {
    try {
      const token = this.getItem('token');
      if (token) {
        await api.post('/api/data_predator/logout/', { token });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.removeItem('token');
      this.removeItem('user');
      this.removeItem('tokenExpiration');
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getItem('token');
    if (!token) {
      return false;
    }
    
    // Check if token is expired
    if (this.isTokenExpired()) {
      // Clean up expired token
      this.logout();
      return false;
    }
    
    return true;
  }

  // Get current user
  getCurrentUser() {
    const user = this.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Get token
  getToken() {
    return this.getItem('token');
  }
}

const authService = new AuthService();
export default authService;
